import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
// Password from environment variable (fallback is for development only)
const PASSWORD = process.env.REACT_APP_AUTH_PASSWORD;

export default function UnifiedController() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [state, setState] = useState(null);
  const [segments, setSegments] = useState([]);
  const [notes, setNotes] = useState("");
  const [editingSegment, setEditingSegment] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [showSegmentEditor, setShowSegmentEditor] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('app_theme') || 'dark');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNotes, setShowNotes] = useState(true);
  const [timerSize, setTimerSize] = useState(() => parseInt(localStorage.getItem('timer_size') || '200'));
  const [breadcrumbSize, setBreadcrumbSize] = useState(() => parseInt(localStorage.getItem('breadcrumb_size') || '100'));
  const [controlsSize, setControlsSize] = useState(() => parseInt(localStorage.getItem('controls_size') || '100'));
  const [showSettings, setShowSettings] = useState(false);
  const [displayTime, setDisplayTime] = useState(0); // For smooth display updates
  const wsRef = useRef(null);
  const displayIntervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const durationRef = useRef(0);
  const stateRef = useRef(null);
  const isAdvancingRef = useRef(false); // Prevent multiple rapid segment changes

  // Save size preferences
  useEffect(() => {
    localStorage.setItem('timer_size', timerSize.toString());
    localStorage.setItem('breadcrumb_size', breadcrumbSize.toString());
    localStorage.setItem('controls_size', controlsSize.toString());
  }, [timerSize, breadcrumbSize, controlsSize]);

  // Save theme
  useEffect(() => {
    localStorage.setItem('app_theme', theme);
  }, [theme]);

  // Password auth
  const handleAuth = () => {
    if (password === PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('auth_token', 'authenticated');
      // Toast removed
    } else {
      // Toast removed
    }
  };

  // Check for saved auth
  useEffect(() => {
    if (localStorage.getItem('auth_token') === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!isAuthenticated) return;

    console.log("Attempting WebSocket connection to:", `${WS_URL}/api/ws`);
    const ws = new WebSocket(`${WS_URL}/api/ws`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("WebSocket connected successfully");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "initial_state") {
        setState(data.state);
        stateRef.current = data.state;
        setSegments(data.segments);
        setNotes(data.state.notes || "");
      } else if (data.type === "state_update") {
        setState(data.state);
        stateRef.current = data.state;
        setNotes(data.state.notes || "");
      } else if (data.type === "segments_update") {
        setSegments(data.segments);
        // Toast removed
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      // Toast removed
    };

    ws.onclose = (event) => {
      console.log("WebSocket disconnected. Code:", event.code, "Reason:", event.reason);
    };

    return () => {
      ws.close();
    };
  }, [isAuthenticated]);

  // Time-based timer - no skipping, smooth display
  useEffect(() => {
    const updateDisplay = () => {
      const currentState = stateRef.current;
      if (!currentState) {
        setDisplayTime(0);
        return;
      }

      if (currentState.is_playing && startTimeRef.current) {
        // Calculate elapsed time since segment started
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, durationRef.current - elapsed / 1000);
        
        setDisplayTime(remaining);
        
        // Check if segment finished - prevent multiple calls
        if (remaining === 0 && durationRef.current > 0 && !isAdvancingRef.current) {
          isAdvancingRef.current = true;
          handleNext().finally(() => {
            // Reset after a delay to prevent rapid firing
            setTimeout(() => {
              isAdvancingRef.current = false;
            }, 500);
          });
        }
      } else {
        // Not playing, show the state's remaining time
        setDisplayTime(currentState.remaining_time);
      }
    };

    // Update display every 100ms for smooth countdown
    const displayInterval = setInterval(updateDisplay, 100);
    displayIntervalRef.current = displayInterval;

    return () => {
      if (displayIntervalRef.current) {
        clearInterval(displayIntervalRef.current);
        displayIntervalRef.current = null;
      }
    };
  }, []);

  // Watch for state changes to reset start time
  useEffect(() => {
    if (state) {
      if (state.is_playing) {
        // Only reset timer when segment changes OR when starting from pause
        // Always update durationRef and startTimeRef to sync with backend
        startTimeRef.current = Date.now();
        durationRef.current = state.remaining_time;
        setDisplayTime(state.remaining_time);
      } else {
        // Paused - update the remaining time from backend
        startTimeRef.current = null;
        durationRef.current = state.remaining_time;
        setDisplayTime(state.remaining_time);
      }
    }
  }, [state?.is_playing, state?.current_segment, state?.remaining_time]);

  // Control functions
  const handleStart = async () => {
    try {
      await axios.post(`${API}/control/start`);
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handlePause = async () => {
    try {
      // Send the current remaining time when pausing
      await axios.post(`${API}/control/pause`, { 
        remaining_time: Math.ceil(displayTime) 
      });
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handleResume = async () => {
    try {
      await axios.post(`${API}/control/resume`);
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handleExtend = async (seconds) => {
    try {
      // Send current display time for accurate addition
      await axios.post(`${API}/control/extend`, { 
        seconds,
        current_time: Math.ceil(displayTime)
      });
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handleNext = async () => {
    try {
      // Reset the advancing flag when manually clicking next
      isAdvancingRef.current = false;
      await axios.post(`${API}/control/next`);
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handleRestart = async () => {
    try {
      await axios.post(`${API}/control/restart`);
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handleNotesUpdate = async (newNotes) => {
    setNotes(newNotes);
    try {
      await axios.post(`${API}/control/update-notes`, { notes: newNotes });
    } catch (error) {
      console.error("Failed to update notes");
    }
  };

  const handleSegmentDurationUpdate = async (segmentId, newDuration) => {
    try {
      await axios.post(`${API}/segments/update`, {
        segment_id: segmentId,
        duration: parseInt(newDuration)
      });
      setEditingSegment(null);
      setEditingField(null);
    } catch (error) {
      // Toast removed
    }
  };

  const handleSegmentNameUpdate = async (segmentId, newName) => {
    try {
      await axios.post(`${API}/segments/update-name`, {
        segment_id: segmentId,
        name: newName
      });
      setEditingSegment(null);
      setEditingField(null);
    } catch (error) {
      // Toast removed
    }
  };

  const handleSegmentRoleUpdate = async (segmentId, newRole) => {
    try {
      await axios.post(`${API}/segments/update-role`, {
        segment_id: segmentId,
        role: newRole
      });
      setEditingSegment(null);
      setEditingField(null);
    } catch (error) {
      // Toast removed
    }
  };

  const handleSegmentColorUpdate = async (segmentId, newColor) => {
    try {
      await axios.post(`${API}/segments/update-color`, {
        segment_id: segmentId,
        color: newColor
      });
      setEditingSegment(null);
      setEditingField(null);
    } catch (error) {
      // Toast removed
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newSegments = [...segments];
    const [draggedItem] = newSegments.splice(draggedIndex, 1);
    newSegments.splice(dropIndex, 0, draggedItem);

    const reorderedSegments = newSegments.map((seg, idx) => ({
      ...seg,
      id: idx
    }));

    setSegments(reorderedSegments);
    setDraggedIndex(null);

    try {
      await axios.post(`${API}/segments/reorder`, {
        segments: reorderedSegments
      });
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleAddSegment = async () => {
    try {
      const newSegment = {
        id: segments.length,
        name: `New Segment ${segments.length + 1}`,
        color: '#6366f1',
        duration: 60,
        role: ""
      };
      
      const newSegments = [...segments, newSegment];
      setSegments(newSegments);
      
      await axios.post(`${API}/segments/reorder`, {
        segments: newSegments
      });
      
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (segments.length <= 1) {
      // Toast removed
      return;
    }
    
    try {
      const newSegments = segments.filter(seg => seg.id !== segmentId);
      const reindexedSegments = newSegments.map((seg, idx) => ({
        ...seg,
        id: idx
      }));
      
      setSegments(reindexedSegments);
      
      await axios.post(`${API}/segments/reorder`, {
        segments: reindexedSegments
      });
      
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleBreadcrumbClick = async (segmentId) => {
    try {
      // Update to clicked segment
      const segment = segments[segmentId];
      await axios.post(`${API}/control/jump-to`, {
        segment_id: segmentId,
        duration: segment.duration
      });
      // Toast removed
    } catch (error) {
      // Toast removed
    }
  };

  const handleTogglePlayback = async () => {
    if (state.is_playing) {
      await handlePause();
    } else {
      if (state.remaining_time === 0) {
        await handleStart();
      } else {
        await handleResume();
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Password screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 max-w-md w-full border border-white/20 shadow-2xl">
          <h1 className="text-4xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            🎬 Bed Scrunchie Live Controller
          </h1>
          <Input
            data-testid="password-input"
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
            className="mb-6 text-2xl text-center py-6 bg-white/20 border-white/30 text-white placeholder:text-white/50"
          />
          <Button
            data-testid="auth-submit-btn"
            onClick={handleAuth}
            className="w-full py-6 text-xl font-bold bg-purple-600 hover:bg-purple-700 rounded-xl"
          >
            Access Controller
          </Button>
        </div>
      </div>
    );
  }

  if (!state || segments.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  const currentSegment = segments[state.current_segment] || segments[0];
  
  // Get ALL segments for breadcrumb - order never changes, just highlight current
  const getBreadcrumbSegments = () => {
    return segments.map((segment, index) => ({
      segment: segment,
      isCurrent: index === state.current_segment,
      position: index - state.current_segment
    }));
  };
  
  const breadcrumbSegments = getBreadcrumbSegments();

  const bgColor = theme === 'dark' ? '#0f172a' : '#f8fafc';
  const textColor = theme === 'dark' ? '#ffffff' : '#1e293b';
  const cardBg = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';

  return (
    <div 
      className="min-h-screen transition-colors duration-300 flex flex-col" 
      style={{ 
        backgroundColor: bgColor,
        fontFamily: 'Space Grotesk, sans-serif',
        paddingBottom: 'env(safe-area-inset-bottom)'
      }}
    >
      {/* Top Controls Bar - Mobile Optimized */}
      <div className="flex justify-between items-center p-2 sm:p-4" style={{ backgroundColor: cardBg }}>
        <h1 className="text-lg sm:text-2xl font-bold truncate" style={{ color: textColor }}>
          🎬 Bed Scrunchie Live Controller
        </h1>
        <div className="flex gap-1 sm:gap-2">
          <Button
            onClick={() => setShowSettings(!showSettings)}
            className="p-1.5 sm:p-2 rounded-xl"
            style={{ backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
            title="Settings & Edit"
          >
            <span className="text-lg sm:text-xl">⚙️</span>
          </Button>
          <Button
            onClick={toggleTheme}
            className="p-1.5 sm:p-2 rounded-xl"
            style={{ backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
            title="Toggle theme"
          >
            <span className="text-lg sm:text-xl">{theme === 'dark' ? '☀️' : '🌙'}</span>
          </Button>
          <Button
            onClick={toggleFullscreen}
            className="p-1.5 sm:p-2 rounded-xl hidden sm:block"
            style={{ backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
            title="Toggle fullscreen"
          >
            <span className="text-lg sm:text-xl">{isFullscreen ? '⊗' : '⛶'}</span>
          </Button>
        </div>
      </div>

      {/* Settings Panel - Full Screen Overlay */}
      {/* Settings Panel - Full Screen Overlay */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-3xl p-8" style={{ backgroundColor: bgColor, border: '3px solid ' + textColor }}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold" style={{ color: textColor }}>⚙️ Settings & Configuration</h2>
              <Button
                onClick={() => setShowSettings(false)}
                className="text-2xl px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl"
              >
                ✕ Close
              </Button>
            </div>

            {/* Size Controls */}
            <div className="mb-8 p-6 rounded-2xl border" style={{ backgroundColor: cardBg, borderColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }}>
              <h3 className="font-bold text-2xl mb-4" style={{ color: textColor }}>⇔ Resize Sections</h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: textColor }}>
                    Timer Font: {timerSize}px
                  </label>
                  <input
                    type="range"
                    min="100"
                    max="300"
                    value={timerSize}
                    onChange={(e) => setTimerSize(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: textColor }}>
                    Breadcrumb: {breadcrumbSize}%
                  </label>
                  <input
                    type="range"
                    min="60"
                    max="140"
                    value={breadcrumbSize}
                    onChange={(e) => setBreadcrumbSize(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: textColor }}>
                    Controls: {controlsSize}%
                  </label>
                  <input
                    type="range"
                    min="70"
                    max="130"
                    value={controlsSize}
                    onChange={(e) => setControlsSize(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
              <Button
                onClick={() => {
                  setTimerSize(200);
                  setBreadcrumbSize(100);
                  setControlsSize(100);
                  // Toast removed
                }}
                className="mt-4 bg-slate-600 hover:bg-slate-500 text-white"
              >
                Reset Defaults
              </Button>
            </div>

            {/* Edit Segments */}
            <div className="p-6 rounded-2xl border" style={{ backgroundColor: cardBg, borderColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-2xl" style={{ color: textColor }}>📋 Edit Segments</h3>
                <Button
                  data-testid="add-segment-btn"
                  onClick={handleAddSegment}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  + Add Segment
                </Button>
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                <p className="text-sm mb-4" style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>
                  💡 Drag to reorder • Click to edit • ✕ to delete
                </p>
                {segments.map((segment, index) => (
                  <div
                    key={segment.id}
                    draggable={editingSegment !== segment.id}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`rounded-xl p-4 flex items-center justify-between transition-all ${
                      draggedIndex === index ? 'opacity-50 scale-95' : ''
                    } ${editingSegment === segment.id ? '' : 'cursor-move hover:opacity-80'}`}
                    style={{ 
                      backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      borderLeft: `6px solid ${segment.color}` 
                    }}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="text-2xl cursor-grab active:cursor-grabbing" style={{ color: textColor }}>⋮⋮</div>
                      <div className="flex-1 space-y-2">
                        {/* Name */}
                        {editingSegment === segment.id && editingField === 'name' ? (
                          <div className="flex gap-2 items-center">
                            <Input
                              data-testid={`name-input-${segment.id}`}
                              type="text"
                              defaultValue={segment.name}
                              className="flex-1 font-bold"
                              placeholder="Segment name"
                              autoFocus
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSegmentNameUpdate(segment.id, e.target.value);
                                }
                              }}
                            />
                            <Button size="sm" onClick={(e) => {
                              const input = e.target.closest('div').querySelector('input');
                              handleSegmentNameUpdate(segment.id, input.value);
                            }} className="bg-green-600 hover:bg-green-700">✓</Button>
                            <Button size="sm" onClick={() => { setEditingSegment(null); setEditingField(null); }} className="bg-slate-600">✕</Button>
                          </div>
                        ) : (
                          <div 
                            className="font-bold text-lg cursor-pointer hover:opacity-70"
                            onClick={() => { setEditingSegment(segment.id); setEditingField('name'); }}
                            style={{ color: textColor }}
                          >
                            {segment.name} ✏️
                          </div>
                        )}
                        
                        {/* Duration */}
                        {editingSegment === segment.id && editingField === 'duration' ? (
                          <div className="flex gap-2 items-center">
                            <Input
                              data-testid={`duration-input-${segment.id}`}
                              type="number"
                              defaultValue={segment.duration}
                              className="w-24"
                              autoFocus
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSegmentDurationUpdate(segment.id, e.target.value);
                                }
                              }}
                            />
                            <span className="text-sm" style={{ color: textColor }}>seconds</span>
                            <Button size="sm" onClick={(e) => {
                              const input = e.target.closest('div').querySelector('input');
                              handleSegmentDurationUpdate(segment.id, input.value);
                            }} className="bg-green-600 hover:bg-green-700">✓</Button>
                            <Button size="sm" onClick={() => { setEditingSegment(null); setEditingField(null); }} className="bg-slate-600">✕</Button>
                          </div>
                        ) : (
                          <div 
                            className="text-sm cursor-pointer hover:opacity-70"
                            onClick={() => { setEditingSegment(segment.id); setEditingField('duration'); }}
                            style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                          >
                            Duration: {formatTime(segment.duration)} ✏️
                          </div>
                        )}

                        {/* Role */}
                        {editingSegment === segment.id && editingField === 'role' ? (
                          <div className="flex gap-2 items-center">
                            <Input
                              data-testid={`role-input-${segment.id}`}
                              type="text"
                              defaultValue={segment.role || ""}
                              className="flex-1"
                              placeholder="Who's responsible? (optional)"
                              autoFocus
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSegmentRoleUpdate(segment.id, e.target.value);
                                }
                              }}
                            />
                            <Button size="sm" onClick={(e) => {
                              const input = e.target.closest('div').querySelector('input');
                              handleSegmentRoleUpdate(segment.id, input.value);
                            }} className="bg-green-600 hover:bg-green-700">✓</Button>
                            <Button size="sm" onClick={() => { setEditingSegment(null); setEditingField(null); }} className="bg-slate-600">✕</Button>
                          </div>
                        ) : (
                          <div 
                            className="text-sm cursor-pointer hover:opacity-70"
                            onClick={() => { setEditingSegment(segment.id); setEditingField('role'); }}
                            style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                          >
                            👤 Role: {segment.role || 'Not assigned'} ✏️
                          </div>
                        )}

                        {/* Color */}
                        {editingSegment === segment.id && editingField === 'color' ? (
                          <div className="flex gap-2 items-center">
                            <input
                              data-testid={`color-input-${segment.id}`}
                              type="color"
                              defaultValue={segment.color || "#6366f1"}
                              className="w-20 h-10 rounded cursor-pointer"
                              autoFocus
                              onBlur={(e) => {
                                handleSegmentColorUpdate(segment.id, e.target.value);
                              }}
                            />
                            <Button size="sm" onClick={(e) => {
                              const input = e.target.closest('div').querySelector('input');
                              handleSegmentColorUpdate(segment.id, input.value);
                            }} className="bg-green-600 hover:bg-green-700">✓</Button>
                            <Button size="sm" onClick={() => { setEditingSegment(null); setEditingField(null); }} className="bg-slate-600">✕</Button>
                          </div>
                        ) : (
                          <div 
                            className="text-sm cursor-pointer hover:opacity-70 flex items-center gap-2"
                            onClick={() => { setEditingSegment(segment.id); setEditingField('color'); }}
                            style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                          >
                            🎨 Color: <span className="inline-block w-6 h-6 rounded border-2 border-white" style={{ backgroundColor: segment.color }}></span> ✏️
                          </div>
                        )}
                      </div>
                    </div>
                    {editingSegment !== segment.id && (
                      <Button
                        data-testid={`delete-segment-${segment.id}`}
                        size="sm"
                        onClick={() => handleDeleteSegment(segment.id)}
                        className="bg-red-600 hover:bg-red-700 ml-2"
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breadcrumb Bar - 5 segments - CLICKABLE - Mobile Optimized */}
      <div 
        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 sm:py-3 overflow-x-auto transition-all scrollbar-hide" 
        style={{ 
          backgroundColor: cardBg,
          transform: `scale(${breadcrumbSize / 100})`,
          transformOrigin: 'top center',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {breadcrumbSegments.map(({ segment, isCurrent, position }) => (
          <div 
            key={`breadcrumb-${segment.id}`}
            onClick={() => !isCurrent && handleBreadcrumbClick(segment.id)}
            className={`flex-1 min-w-[80px] sm:min-w-[120px] text-center py-2 sm:py-3 rounded-lg sm:rounded-xl transition-all touch-manipulation ${
              isCurrent ? 'shadow-2xl scale-105' : 'cursor-pointer active:scale-95 hover:scale-105 hover:opacity-100'
            }`}
            style={{ 
              backgroundColor: segment.color, 
              opacity: isCurrent ? 1 : 0.7,
              border: isCurrent ? `${window.innerWidth < 640 ? '2px' : '4px'} solid ` + textColor : 'none'
            }}
          >
            {segment.role && (
              <div 
                className="text-[10px] sm:text-xs mb-0.5 sm:mb-1 font-semibold"
                style={{ color: segment.color === '#f8f8f8' ? '#000' : '#fff', opacity: 0.9 }}
              >
                👤 {segment.role}
              </div>
            )}
            <div 
              className={`${isCurrent ? 'text-sm sm:text-lg' : 'text-xs sm:text-sm'} font-bold leading-tight`}
              style={{ color: segment.color === '#f8f8f8' ? '#000' : '#fff' }}
            >
              {segment.name}
            </div>
          </div>
        ))}
      </div>

      {/* MASSIVE TIMER - Full width at top - Mobile Optimized - Increased height */}
      <div
        className="rounded-2xl sm:rounded-3xl shadow-2xl flex flex-col items-center justify-center relative overflow-hidden mx-2 sm:mx-4 mb-3 sm:mb-4 transition-all"
        style={{ 
          backgroundColor: currentSegment.color, 
          border: `${window.innerWidth < 640 ? '4px' : '8px'} solid ` + (theme === 'dark' ? '#fff' : '#000'),
          minHeight: window.innerWidth < 640 ? '350px' : '500px',
          flex: '0 0 auto'
        }}
        data-testid="main-timer-display"
      >
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            background: `radial-gradient(circle at 30% 30%, white 0%, transparent 60%)`
          }}
        />
        
        <div className="text-center z-10 px-4 sm:px-8 w-full">
          {/* Role name with LIVE indicator on same line - Mobile Responsive */}
          <div className="flex items-center justify-center gap-4 sm:gap-8 mb-2 sm:mb-3 flex-wrap">
            {currentSegment.role && (
              <div 
                className="font-bold transition-all"
                style={{ 
                  fontSize: `${Math.floor(timerSize * 0.15)}px`,
                  color: currentSegment.color === '#f8f8f8' ? '#000' : '#fff', 
                  opacity: 0.9
                }}
                data-testid="segment-role"
              >
                👤 {currentSegment.role}
              </div>
            )}
            {state.is_playing && (
              <div 
                className="font-bold animate-pulse transition-all"
                style={{ 
                  fontSize: `${Math.floor(timerSize * 0.15)}px`,
                  color: currentSegment.color === '#f8f8f8' ? '#000' : '#fff' 
                }}
              >
                ▶️ LIVE
              </div>
            )}
          </div>
          
          {/* TIMER - Scales with viewport - Monospaced - Time-based */}
          <div 
            className="font-bold mb-2 sm:mb-4" 
            style={{ 
              fontSize: window.innerWidth < 640 ? `${Math.floor(timerSize * 0.4)}px` : `${timerSize}px`,
              lineHeight: '1',
              color: currentSegment.color === '#f8f8f8' ? '#000' : '#fff',
              textShadow: currentSegment.color === '#f8f8f8' ? 'none' : '0 8px 32px rgba(0,0,0,0.4)',
              fontFamily: '"Roboto Mono", "SF Mono", "Courier New", monospace',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.05em'
            }}
            data-testid="countdown-timer"
          >
            {formatTime(displayTime)}
          </div>
          
          {/* Segment Title - Mobile Responsive */}
          <div 
            className="font-bold transition-all"
            style={{ 
              fontSize: window.innerWidth < 640 ? `${Math.floor(timerSize * 0.15)}px` : `${Math.floor(timerSize * 0.3)}px`,
              color: currentSegment.color === '#f8f8f8' ? '#000' : '#fff' 
            }}
            data-testid="segment-title"
          >
            {currentSegment.name}
          </div>
        </div>
      </div>

      {/* Unified Controls - Fully Responsive with Wrapping */}
      <div 
        className="px-2 sm:px-4 pb-20 sm:pb-6 transition-all" 
        style={{ transform: `scale(${controlsSize / 100})`, transformOrigin: 'bottom center' }}
      >
        <div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl border" style={{ backgroundColor: cardBg, borderColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }}>
          {/* Responsive flex-wrap layout */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {/* Play/Pause Toggle - Always first */}
            <Button
              data-testid="toggle-playback-btn"
              onClick={handleTogglePlayback}
              className="flex-shrink-0 py-4 px-8 text-lg sm:text-xl font-bold bg-slate-700 hover:bg-slate-600 active:bg-slate-800 rounded-xl text-white touch-manipulation"
            >
              {state.is_playing ? '⏸️ Pause' : '▶️ Play'}
            </Button>
            
            {/* Extend Buttons - Group together */}
            <Button
              data-testid="extend-15-btn"
              onClick={() => handleExtend(15)}
              className="flex-shrink-0 py-4 px-5 text-sm sm:text-base font-bold bg-slate-600 hover:bg-slate-500 active:bg-slate-700 rounded-xl text-white touch-manipulation"
            >
              +15s
            </Button>
            <Button
              data-testid="extend-30-btn"
              onClick={() => handleExtend(30)}
              className="flex-shrink-0 py-4 px-5 text-sm sm:text-base font-bold bg-slate-600 hover:bg-slate-500 active:bg-slate-700 rounded-xl text-white touch-manipulation"
            >
              +30s
            </Button>
            <Button
              data-testid="extend-45-btn"
              onClick={() => handleExtend(45)}
              className="flex-shrink-0 py-4 px-5 text-sm sm:text-base font-bold bg-slate-600 hover:bg-slate-500 active:bg-slate-700 rounded-xl text-white touch-manipulation"
            >
              +45s
            </Button>

            {/* Segment Navigation - Will wrap to next line on small screens */}
            <Button
              data-testid="restart-btn"
              onClick={handleRestart}
              className="flex-shrink-0 py-4 px-6 text-base sm:text-lg font-bold bg-slate-600 hover:bg-slate-500 active:bg-slate-700 rounded-xl text-white touch-manipulation"
            >
              🔄 Restart
            </Button>
            <Button
              data-testid="next-btn"
              onClick={handleNext}
              className="flex-shrink-0 py-4 px-6 text-base sm:text-lg font-bold bg-slate-600 hover:bg-slate-500 active:bg-slate-700 rounded-xl text-white touch-manipulation"
            >
              ⏭️ Next
            </Button>
          </div>
        </div>
      </div>

    </div>
  );
}
